import Phaser from 'phaser';
import {
  GAME_WIDTH,
  getGameHeight,
} from '../config/constants';
import { BoardSystem, type Barricade } from '../systems/BoardSystem';
import { BoardRenderer } from './BoardRenderer';
import { soundManager } from '../audio/SoundManager';

/**
 * Animates hammer strikes against barricades. The hammer flies from
 * the queue area to the barricade with an arc motion, then impacts
 * with particles and screen shake.
 *
 * GameScene delegates hammer animation to this class.
 */
export class HammerAnimator {
  private scene: Phaser.Scene;
  private board: BoardSystem;
  private boardRenderer: BoardRenderer;
  private onComplete: () => void;

  /** True while a hammer strike animation is playing -- blocks queue processing. */
  hammerAnimating = false;

  constructor(
    scene: Phaser.Scene,
    board: BoardSystem,
    boardRenderer: BoardRenderer,
    onComplete: () => void,
  ) {
    this.scene = scene;
    this.board = board;
    this.boardRenderer = boardRenderer;
    this.onComplete = onComplete;
  }

  /**
   * Animate a hammer striking a barricade. The hammer flies from the queue area
   * to the barricade with an arc motion, then impacts with particles and screen shake.
   */
  animateHammerStrike(barricade: Barricade): void {
    this.hammerAnimating = true;

    const barricadeEntry = this.boardRenderer.barricadeSprites.get(barricade.id);
    if (!barricadeEntry) {
      this.hammerAnimating = false;
      return;
    }

    const targetX = barricadeEntry.container.x;
    const targetY = barricadeEntry.container.y;

    // Start from queue area (bottom center)
    const startX = GAME_WIDTH / 2;
    const startY = getGameHeight() - 80;

    // Create hammer sprite
    const hammerContainer = this.scene.add.container(startX, startY).setDepth(20);

    // Hammer head (gray rectangle)
    const head = this.scene.add.graphics();
    head.fillStyle(0x888888, 1);
    head.fillRoundedRect(-16, -24, 32, 24, 4);
    head.lineStyle(2, 0x666666, 1);
    head.strokeRoundedRect(-16, -24, 32, 24, 4);
    hammerContainer.add(head);

    // Hammer handle (brown rectangle)
    const handle = this.scene.add.graphics();
    handle.fillStyle(0x8B6914, 1);
    handle.fillRect(-4, 0, 8, 32);
    hammerContainer.add(handle);

    // Arc tween: fly to barricade with arc
    const midX = (startX + targetX) / 2;
    const midY = Math.min(startY, targetY) - 160; // arc above

    const duration = 400;

    // Animate along a bezier-like arc (using two sequential tweens)
    this.scene.tweens.add({
      targets: hammerContainer,
      x: midX,
      y: midY,
      rotation: -Math.PI * 0.5,
      scaleX: 1.5,
      scaleY: 1.5,
      duration: duration / 2,
      ease: 'Sine.easeOut',
      onComplete: () => {
        // Second half: swing down to target
        this.scene.tweens.add({
          targets: hammerContainer,
          x: targetX,
          y: targetY,
          rotation: Math.PI * 0.25,
          scaleX: 1.0,
          scaleY: 1.0,
          duration: duration / 2,
          ease: 'Sine.easeIn',
          onComplete: () => {
            // Impact!
            hammerContainer.destroy();

            // Screen shake
            this.scene.cameras.main.shake(150, 0.008);

            // Impact particles
            const emitter = this.scene.add.particles(targetX, targetY, 'particle', {
              speed: { min: 40, max: 120 },
              scale: { start: 1.0, end: 0 },
              alpha: { start: 1, end: 0 },
              lifespan: 400,
              tint: [0x8B6914, 0x888888, 0xffffff],
              emitting: false,
            });
            emitter.explode(16);
            this.scene.time.delayedCall(500, () => emitter.destroy());

            soundManager.randomPop();

            // Apply damage to barricade
            const result = this.board.hitBarricade(barricade.id);

            if (result.destroyed) {
              // Barricade is destroyed -- play shatter animation
              this.boardRenderer.handleBarricadeDestroy(barricade);
            } else {
              // Update HP display
              this.boardRenderer.handleBarricadeHpUpdate(barricade.id, result.hp, barricade.maxHp);
            }

            // Resume normal processing after a short delay
            this.scene.time.delayedCall(200, () => {
              this.hammerAnimating = false;
              this.onComplete();
            });
          },
        });
      },
    });
  }
}
